/* ── Shared voice helpers ───────────────────────────────────────────────────
   One place for the soft, slow, Indian-English female voice used by both the
   per-message "Read aloud" button and the continuous voice-conversation mode.
   ─────────────────────────────────────────────────────────────────────────── */

/* Pick the warmest available Indian-English female voice, with sensible fallbacks. */
export function pickIndianFemaleVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  const female = (v) =>
    /female|woman|zira|heera|priya|neerja|kalpana|aditi|raveena|swara|female/i.test(
      `${v.name}`
    );
  return (
    voices.find((v) => v.lang === "en-IN" && female(v)) ||
    voices.find((v) => v.lang === "en-IN") ||
    voices.find((v) => v.lang === "hi-IN") ||
    voices.find((v) => /^en(-|_)/i.test(v.lang) && female(v)) ||
    voices.find((v) => /^en(-|_)/i.test(v.lang)) ||
    voices[0] ||
    null
  );
}

/* Speak `text` in the soft, slow Indian voice. Strips markdown so it reads naturally.
   Returns the utterance. opts: { onstart, onend, onerror, rate, pitch, volume }. */
export function speakText(text, opts = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis || !text) return null;
  const synth = window.speechSynthesis;
  const clean = String(text)
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#*_`>|]/g, " ")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return null;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(clean);
  const v = pickIndianFemaleVoice();
  if (v) {
    u.voice = v;
    u.lang = v.lang;
  } else {
    u.lang = "en-IN";
  }
  u.rate = opts.rate ?? 0.88; // slow, easy to follow
  u.pitch = opts.pitch ?? 1.05; // soft, natural
  u.volume = opts.volume ?? 0.9; // gentle
  if (opts.onstart) u.onstart = opts.onstart;
  if (opts.onend) u.onend = opts.onend;
  if (opts.onerror) u.onerror = opts.onerror;
  synth.speak(u);
  return u;
}

export function stopSpeaking() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}
